<template>
  <button
    type="button"
    class="c-button-white -copy -prefix-icon"
    :class="{
      '-small': small,
      'is-copied': copyState === 'copied',
      'is-copy-failed': copyState === 'failed'
    }"
    @click="handleClick"
  >
    <span
      class="label"
      aria-live="polite"
    >{{ labelText }}</span>
  </button>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref } from "vue";
import {
  COPY_TABLE_FEEDBACK_DURATION_MS,
  COPY_TABLE_FEEDBACK_LABEL,
  copyTableToClipboard
} from "@/util/copy-table-to-clipboard";

const DEFAULT_LABEL = "表をコピー";

const props = defineProps<{
  targetTable: HTMLTableElement | null;
  // まわりの文字が小さい場所（テスト結果の表ヘッダー内など）に置く場合の小さい版
  small?: boolean;
}>();

const copyState = ref<"idle" | "copied" | "failed">("idle");
// 「元の表示に戻す」タイマー。連打された場合に前回分をキャンセルするため保持しておく。
let resetTimerId: ReturnType<typeof setTimeout> | null = null;
// コピーの処理（Promise）は、コンポーネントをアンマウントしても中断されずに進み続けるため、
// アンマウント後に完了した場合はフィードバック表示を更新しないようにする
let isUnmounted = false;

const labelText = computed(() => {
  if (copyState.value === "copied") return COPY_TABLE_FEEDBACK_LABEL.copied;
  if (copyState.value === "failed") return COPY_TABLE_FEEDBACK_LABEL.failed;
  return DEFAULT_LABEL;
});

onUnmounted(() => {
  isUnmounted = true;
  clearTimeout(resetTimerId);
});

function showFeedback(state: "copied" | "failed") {
  if (isUnmounted) return;
  clearTimeout(resetTimerId);
  copyState.value = state;
  resetTimerId = setTimeout(() => {
    copyState.value = "idle";
    resetTimerId = null;
  }, COPY_TABLE_FEEDBACK_DURATION_MS);
}

function handleClick() {
  if (!props.targetTable) return;
  copyTableToClipboard(props.targetTable)
    .then(() => showFeedback("copied"))
    .catch(() => showFeedback("failed"));
}
</script>
